/**
 * Pure helper functions for whiteboard auto-open, badge filtering, and event
 * processing.  Extracted from LiveKitVideoDisplayController so the decision
 * logic can be unit-tested without mounting the full React tree.
 */

// ---------------------------------------------------------------------------
// Badge-set computation
// ---------------------------------------------------------------------------

/**
 * Build the set of session IDs that should display a whiteboard badge.
 * Only users on VR-scoped whiteboards in the *same* virtual room as the
 * local player are included.  Public whiteboards (no vrId) never show badges.
 */
export function computeWhiteboardBadgeSet(
  activeWhiteboardUsers: Record<string, string[]>,
  whiteboardVrIds: Record<string, string>,
  localVrId: string | null,
): Set<string> {
  const set = new Set<string>()
  for (const [wbId, users] of Object.entries(activeWhiteboardUsers)) {
    const wbVrId = whiteboardVrIds[wbId]
    if (!wbVrId) continue
    if (wbVrId !== localVrId) continue
    for (const sid of users) set.add(sid)
  }
  return set
}

// ---------------------------------------------------------------------------
// Badge-click lookup
// ---------------------------------------------------------------------------

/**
 * Find the first whiteboard that contains a given participant.
 * Returns the whiteboard ID or `null` if the participant is not active on any.
 */
export function findWhiteboardForParticipant(
  activeWhiteboardUsers: Record<string, string[]>,
  participantId: string,
): string | null {
  for (const [wbId, users] of Object.entries(activeWhiteboardUsers)) {
    if (users.includes(participantId)) return wbId
  }
  return null
}

// ---------------------------------------------------------------------------
// Auto-open detection
// ---------------------------------------------------------------------------

export interface AutoOpenCandidate {
  whiteboardId: string
  newUsers: string[]
}

/**
 * Detect whether a whiteboard in the local user's VR gained new members and
 * should be auto-opened.  Returns the first qualifying whiteboard + its new
 * users, or `null` if nothing qualifies.
 *
 * Rules:
 * - Public whiteboards (no vrId) never auto-open.
 * - Whiteboards in a different VR never auto-open.
 * - Only whiteboards with genuinely *new* users (not in `prevUsers`) qualify.
 */
export function detectAutoOpenWhiteboard(
  activeWhiteboardUsers: Record<string, string[]>,
  prevWhiteboardUsers: Record<string, string[]>,
  whiteboardVrIds: Record<string, string>,
  localVrId: string | null,
): AutoOpenCandidate | null {
  for (const [wbId, users] of Object.entries(activeWhiteboardUsers)) {
    const wbVrId = whiteboardVrIds[wbId]
    if (!wbVrId) continue
    if (wbVrId !== localVrId) continue

    const prev = prevWhiteboardUsers[wbId] || []
    const newUsers = users.filter((u) => !prev.includes(u))
    if (newUsers.length > 0) {
      return { whiteboardId: wbId, newUsers }
    }
  }
  return null
}

// ---------------------------------------------------------------------------
// Debounce guard
// ---------------------------------------------------------------------------

/**
 * Returns `true` when a removal event arrived within 3 s of the corresponding
 * add — indicating a spurious server-side cleanup rather than a real disconnect.
 */
export function isSpuriousRemoval(
  addTimestamps: Map<string, number>,
  key: string,
  now: number = Date.now(),
  windowMs: number = 3000,
): boolean {
  const addedAt = addTimestamps.get(key)
  return addedAt !== undefined && now - addedAt < windowMs
}
